import { roomNameKey } from '../../../api/src/modules/v3/room-name-key';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type HotelPictureView } from '../api';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { ODA_SAYFA_BOYUTU, odalariGrupla, seciliOdaSayfasi } from './oda-gruplama';
import type { OdaSecenegi } from './tipler';

function Galeri({ ad, kareler }: { ad: string; kareler: readonly HotelPictureView[] }) {
  const [sira, setSira] = useState(0);
  const [bozuk, setBozuk] = useState(false);
  const kare = kareler[sira];
  if (!kare || bozuk) return <p className="ht-ipucu">{M.odaGaleriYok}</p>;
  return <div className="ht-oda-galeri" aria-label={M.odaGaleri(ad)}>
    <img key={kare.src} src={kare.src} srcSet={kare.srcset || undefined} sizes="(max-width: 600px) 90vw, 320px" alt={kare.alt || ad}
      loading="lazy" decoding="async" width={320} height={200} onError={() => setBozuk(true)} />
    {kareler.length > 1 ? <div className="ht-galeri-gezinme">
      <button type="button" className="ht-btn" disabled={sira === 0} onClick={() => setSira(n => n - 1)} aria-label={M.odaOncekiFoto}>‹</button>
      <span aria-live="polite">{sira + 1} / {kareler.length}</span>
      <button type="button" className="ht-btn" disabled={sira === kareler.length - 1} onClick={() => setSira(n => n + 1)} aria-label={M.odaSonrakiFoto}>›</button>
    </div> : null}
  </div>;
}

/** Only this page is mounted. Room offers render before the deferred metadata request. */
export function OdaListesi({ slug, secenekler, secili, sifirlama, children }: {
  slug: string | null; secenekler: readonly OdaSecenegi[]; secili: string | null; sifirlama: number;
  children: (secenek: OdaSecenegi) => ReactNode;
}) {
  const odalar = useMemo(() => odalariGrupla(secenekler), [secenekler]);
  const [sayfa, setSayfa] = useState(() => seciliOdaSayfasi(odalar, secili));
  const kap = useRef<HTMLDivElement>(null);
  useEffect(() => { setSayfa(seciliOdaSayfasi(odalar, secili)); }, [odalar, secili, sifirlama]);
  const sayfaSayisi = Math.max(1, Math.ceil(odalar.length / ODA_SAYFA_BOYUTU));
  const mevcut = Math.min(sayfa, sayfaSayisi - 1);
  const gorunen = odalar.slice(mevcut * ODA_SAYFA_BOYUTU, (mevcut + 1) * ODA_SAYFA_BOYUTU);
  const fotografIcinUygun = gorunen.filter(oda => odalar.filter(diger => roomNameKey(diger.ad) === roomNameKey(oda.ad)).length === 1);
  const adlar = JSON.stringify([...new Set(fotografIcinUygun.map(oda => oda.ad))]);
  const [sonuc, setSonuc] = useState<{ key: string; rooms: { name: string; photos: HotelPictureView[] }[] } | null>(null);
  const key = `${slug ?? ''}:${adlar}`;
  useEffect(() => {
    if (!slug || adlar === '[]') return;
    const controller = new AbortController();
    // A new task after paint keeps slow photo metadata out of the offer rendering path.
    const timer = window.setTimeout(() => {
      void api.hotelRoomGallery(slug, JSON.parse(adlar) as string[], controller.signal)
        .then(result => { if (!controller.signal.aborted) setSonuc({ key, rooms: result.rooms }); })
        .catch(() => { if (!controller.signal.aborted) setSonuc({ key, rooms: [] }); });
    }, 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [slug, adlar, key]);
  const pageChange = (next: number) => {
    setSayfa(next);
    kap.current?.focus();
  };
  return <div className="ht-oda-listesi" ref={kap} tabIndex={-1}>
    {gorunen.map(oda => <section key={oda.anahtar} className="ht-fiziksel-oda" data-testid="ht-fiziksel-oda" aria-label={oda.ad}>
      <div className="ht-fiziksel-oda-ust"><h4>{oda.ad}</h4><span className="ht-ipucu">{M.odaFiyatSayisi(oda.secenekler.length)}</span></div>
      {sonuc?.key === key || !fotografIcinUygun.includes(oda) ? <Galeri key={`${key}:${oda.anahtar}`} ad={oda.ad} kareler={sonuc?.key === key && fotografIcinUygun.includes(oda) ? sonuc.rooms.find(room => room.name === oda.ad)?.photos ?? [] : []} /> : null}
      <div className="ht-oda-fiyatlar">{oda.secenekler.map(children)}</div>
    </section>)}
    {sayfaSayisi > 1 ? <nav className="ht-oda-sayfalama" aria-label={M.odaSayfalama}>
      <button type="button" className="ht-btn" disabled={mevcut === 0} onClick={() => pageChange(mevcut - 1)}>{M.odaOnceki}</button>
      <span aria-live="polite">{M.odaSayfa(mevcut + 1, sayfaSayisi, odalar.length)}</span>
      <button type="button" className="ht-btn" disabled={mevcut + 1 === sayfaSayisi} onClick={() => pageChange(mevcut + 1)}>{M.odaSonraki}</button>
      {seciliOdaSayfasi(odalar, secili) !== mevcut ? <button type="button" className="ht-baglanti" onClick={() => pageChange(seciliOdaSayfasi(odalar, secili))}>{M.odaSeciliyeGit}</button> : null}
    </nav> : null}
  </div>;
}
